<!-- 0.1.129; updated 2026-10-02 15:29:49 CST; parent f142a195884a186a912053e3a5175725bad58984. -->
<!-- HotX Console 0.1.117 专属套餐展示收口。实际修改时间：2026-09-26 00:10:00 CST；精确父基线：c02d398d5aa41ddf9f2ca5b0d6afd09176568092。 -->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { pointApi, pointErrorMessage, type DomainMonthlyPlan, type DomainPlanItem, type DomainPlanOverview } from '../api/pointApi'
import {
  assignedRootPartitionForDomain,
  defaultPartitionForDomain,
  partitionDisplayName,
  partitionOptions,
  partitionStatusLabel,
  quotaModeLabel,
  suspensionReasonLabel,
  type DomainPackageSelection,
  type DomainPlanPartitionOption,
  type DomainPlanPartition
} from '../features/domain-plan/domainPlanPartitions'
import { classifyDomainPlanError } from '../features/domain-plan/domainPlanSubmission'
import {
  openRechargeWindow,
  subscribeRechargeSuccess,
  type RechargeNetwork
} from '../features/domain-plan/rechargeReturn'
import { usePointStore } from '../stores/points'
import { displayThreeDecimal } from '../utils/formatters'
import { hasConsoleSession } from '../api/client'

const points = usePointStore()
const overview = ref<DomainPlanOverview | null>(null)
const loading = ref(false)
const actionKey = ref('')
const selectedPackageByDomain = ref<Record<number, string>>({})
const periodsByDomain = ref<Record<number, number>>({})
const purchaseLockedUntil = ref(0)
const purchaseLockSeconds = ref(0)
const packageFailure = ref<ReturnType<typeof classifyDomainPlanError> | null>(null)
const selectedMigrationTargetByPartition = ref<Record<number, number>>({})
const selectedMigrationDomainByPartition = ref<Record<number, number>>({})
const periodOptions = [1, 6, 12] as const
const DOMAIN_PURCHASE_INTENT_STORAGE_KEY = 'hotx.packages.domain-purchase-intents.v1'
const PARTITION_RENEW_INTENT_STORAGE_KEY = 'hotx.packages.partition-renew-intents.v1'
const MAX_STORED_PURCHASE_INTENTS = 40
const MAX_STORED_RENEW_INTENTS = 40
let purchaseLockTimer: number | undefined

function restoreDomainPurchaseIntentKeys() {
  const restored = new Map<string, string>()
  if (typeof window === 'undefined') return restored
  try {
    const raw = window.sessionStorage.getItem(DOMAIN_PURCHASE_INTENT_STORAGE_KEY)
    if (!raw) return restored
    const parsed = JSON.parse(raw) as { version?: unknown; entries?: unknown }
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) throw new Error('invalid purchase intent storage')
    for (const entry of parsed.entries.slice(-MAX_STORED_PURCHASE_INTENTS)) {
      if (!Array.isArray(entry) || entry.length !== 2) continue
      const [signature, key] = entry
      if (typeof signature === 'string' && signature && typeof key === 'string' && key) {
        restored.set(signature, key)
      }
    }
  } catch {
    try {
      window.sessionStorage.removeItem(DOMAIN_PURCHASE_INTENT_STORAGE_KEY)
    } catch {
      // Browser storage policy must not block package operations.
    }
  }
  return restored
}

const domainPurchaseIntentKeys = restoreDomainPurchaseIntentKeys()

function persistDomainPurchaseIntentKeys() {
  if (typeof window === 'undefined') return
  try {
    const entries = [...domainPurchaseIntentKeys.entries()].slice(-MAX_STORED_PURCHASE_INTENTS)
    if (!entries.length) {
      window.sessionStorage.removeItem(DOMAIN_PURCHASE_INTENT_STORAGE_KEY)
      return
    }
    window.sessionStorage.setItem(DOMAIN_PURCHASE_INTENT_STORAGE_KEY, JSON.stringify({ version: 1, entries }))
  } catch {
    // The in-memory key still protects retries during the current page lifetime.
  }
}

function restorePartitionRenewIntentKeys() {
  const restored = new Map<string, string>()
  if (typeof window === 'undefined') return restored
  try {
    const raw = window.sessionStorage.getItem(PARTITION_RENEW_INTENT_STORAGE_KEY)
    if (!raw) return restored
    const parsed = JSON.parse(raw) as { version?: unknown; entries?: unknown }
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) throw new Error('invalid renew intent storage')
    for (const entry of parsed.entries.slice(-MAX_STORED_RENEW_INTENTS)) {
      if (!Array.isArray(entry) || entry.length !== 2) continue
      const [signature, key] = entry
      if (typeof signature === 'string' && signature && typeof key === 'string' && key) {
        restored.set(signature, key)
      }
    }
  } catch {
    try {
      window.sessionStorage.removeItem(PARTITION_RENEW_INTENT_STORAGE_KEY)
    } catch {
      // Browser storage policy must not block partition renewals.
    }
  }
  return restored
}

const partitionRenewIntentKeys = restorePartitionRenewIntentKeys()
const renewingPartitionIds = new Set<number>()

function persistPartitionRenewIntentKeys() {
  if (typeof window === 'undefined') return
  try {
    const entries = [...partitionRenewIntentKeys.entries()].slice(-MAX_STORED_RENEW_INTENTS)
    if (!entries.length) {
      window.sessionStorage.removeItem(PARTITION_RENEW_INTENT_STORAGE_KEY)
      return
    }
    window.sessionStorage.setItem(PARTITION_RENEW_INTENT_STORAGE_KEY, JSON.stringify({ version: 1, entries }))
  } catch {
    // The in-memory key still protects retries during the current page lifetime.
  }
}

function partitionRenewIntent(partitionId: number, periodCount: number) {
  const signature = `${partitionId}:${periodCount}`
  const partitionPrefix = `${partitionId}:`
  for (const storedSignature of partitionRenewIntentKeys.keys()) {
    if (storedSignature.startsWith(partitionPrefix) && storedSignature !== signature) {
      partitionRenewIntentKeys.delete(storedSignature)
    }
  }
  const key = partitionRenewIntentKeys.get(signature)
    || `partition-renew-${partitionId}-${periodCount}-${Date.now()}-${Math.random().toString(36).slice(2)}`
  partitionRenewIntentKeys.set(signature, key)
  persistPartitionRenewIntentKeys()
  return { signature, key }
}

const domains = computed(() => overview.value?.domains || [])
const activePush = computed(() => overview.value?.activePush || null)
const monthlyPlans = computed(() => overview.value?.plans || [])
const partitions = computed(() => overview.value?.partitions || [])
const packageOptions = computed(() => partitionOptions(overview.value))
const purchaseLocked = computed(() => purchaseLockSeconds.value > 0)
const guestMode = computed(() => !hasConsoleSession())
const domainEmptyDescription = computed(() => guestMode.value
  ? '当前未登录，登录后可查看已接入域名及对应套餐。'
  : '暂无域名，请先在域名管理中接入域名。')

function money(value?: number | null) {
  return displayThreeDecimal(value)
}

function dateText(value?: string | null) {
  if (!value) return '未设置'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

function periodLabel(value?: string | null) {
  return value === 'MONTH' || value === 'CALENDAR_MONTH' ? '月' : '周期'
}

function billingLabel(domain: DomainPlanItem) {
  if (domain.billingMode === 'ADMIN_PUSH') return `专属定制套餐 / ${domain.planName || '定制方案'}`
  if (domain.billingMode === 'PARTITION') return `购买套餐 / ${domain.partitionDisplayName || domain.planName || '分区'}`
  if (domain.billingMode === 'MONTHLY') return `购买套餐 / ${domain.planName || '未命名套餐'}`
  return `免费套餐 / ${domain.planName || '免费套餐'}`
}

function statusLabel(domain: DomainPlanItem) {
  if (domain.status === 'PROTECTION') return domain.billingMode === 'ADMIN_PUSH' ? '专属保护期' : '保护期'
  if (domain.status === 'DELETED') return '已删除'
  return '使用中'
}

function statusType(domain: DomainPlanItem) {
  if (domain.status === 'PROTECTION') return 'warning'
  if (domain.status === 'DELETED') return 'danger'
  return 'success'
}

function selectedPeriods(domainId: number) {
  return periodsByDomain.value[domainId] || 1
}

function selectedPackageOption(domain: DomainPlanItem) {
  const selectedKey = selectedPackageByDomain.value[domain.domainId]
  return packageOptions.value.find(option => option.key === selectedKey) || null
}

function selectedMonthlyPlan(domain: DomainPlanItem) {
  const selected = selectedPackageOption(domain)
  return monthlyPlans.value.find(plan => plan.id === selected?.planId) || null
}

function selectedPackageSelection(domain: DomainPlanItem): DomainPackageSelection | null {
  const option = selectedPackageOption(domain)
  if (!option) return null
  if (option.selection.mode === 'EXISTING') return option.selection
  return {
    ...option.selection,
    periodCount: selectedPeriods(domain.domainId)
  }
}

function packageSelectionHint(domain: DomainPlanItem) {
  const option = selectedPackageOption(domain)
  if (!option) return '请选择套餐分区。'
  if (option.unavailableReason) {
    return `原套餐分区${option.unavailableReason}，请先续费或处理原分区。`
  }
  return option.isNew
    ? '将购买新的独立套餐分区，确认后按所选周期扣除积分。'
    : '使用已有分区剩余额度，本次不扣积分。'
}

// 2026-09-06 CST: purchase discounts are intentionally fixed here to mirror
// backend domain monthly billing: 6 months = 90%, 12 months = 85%.
function discountRate(months: number) {
  if (months >= 12) return 0.85
  if (months >= 6) return 0.9
  return 1
}

function quote(plan: DomainMonthlyPlan | null, months: number) {
  if (!plan) return 0
  return Number(plan.pointPrice || 0) * months * discountRate(months)
}

function initDomainSelectors(data: DomainPlanOverview) {
  for (const domain of data.domains || []) {
    if (!periodsByDomain.value[domain.domainId]) periodsByDomain.value[domain.domainId] = 1
    const currentKey = selectedPackageByDomain.value[domain.domainId]
    if (currentKey && partitionOptions(data).some(option => option.key === currentKey)) continue
    const firstPlan = data.plans?.[0]
    if (firstPlan) {
      const existing = defaultPartitionForDomain(domain.domain, data, firstPlan.id)
      selectedPackageByDomain.value[domain.domainId] = existing
        ? `PARTITION:EXISTING:${existing.id}`
        : `PARTITION:NEW:${firstPlan.id}`
    }
  }
}

function handlePackageSelectionChange(domain: DomainPlanItem, selectedKey: string) {
  const option = packageOptions.value.find(item => item.key === selectedKey)
  if (!option) return
  const assigned = assignedRootPartitionForDomain(domain.domain, overview.value, option.planId)
  if (assigned && option.id !== assigned.id) {
    selectedPackageByDomain.value[domain.domainId] = `PARTITION:EXISTING:${assigned.id}`
    ElMessage.warning(`同一根域名必须继续使用 ${partitionDisplayName(assigned)}，请先续费或处理原分区。`)
  }
}

async function load() {
  if (!hasConsoleSession()) {
    loading.value = false
    return
  }
  loading.value = true
  const [overviewResult, pointsResult] = await Promise.allSettled([
    pointApi.domainPlans(),
    points.refresh()
  ])
  if (overviewResult.status === 'fulfilled') {
    applyOverview(overviewResult.value)
    initDomainSelectors(overviewResult.value)
  } else {
    ElMessage.error(pointErrorMessage(overviewResult.reason, '套餐信息加载失败，请稍后重试。'))
  }
  if (pointsResult.status === 'rejected') {
    ElMessage.warning('套餐已加载，但积分余额刷新失败，请手动刷新。')
  }
  loading.value = false
}

function applyOverview(value: DomainPlanOverview) {
  overview.value = value
  for (const partition of value.partitions || []) {
    const target = migrationTargets(partition)[0]
    if (target && !selectedMigrationTargetByPartition.value[partition.id]) {
      selectedMigrationTargetByPartition.value[partition.id] = target.id
    }
    const domain = migrationDomains(partition)[0]
    if (domain && !selectedMigrationDomainByPartition.value[partition.id]) {
      selectedMigrationDomainByPartition.value[partition.id] = domain.domainId
    }
  }
}

function applyPartition(value: DomainPlanPartition) {
  if (!overview.value) return
  overview.value = {
    ...overview.value,
    partitions: (overview.value.partitions || []).map(partition =>
      partition.id === value.id ? value : partition
    )
  }
}

function showPartitionFailure(reason: unknown) {
  packageFailure.value = classifyDomainPlanError(reason)
  ElMessage.error(packageFailure.value.message)
}

function chooseRechargeNetwork(network: RechargeNetwork) {
  openRechargeWindow(network)
}

const stopRechargeReturn = subscribeRechargeSuccess(() => {
  void points.refresh().then(() => {
    if (packageFailure.value?.rechargeRequired) {
      packageFailure.value = {
        ...packageFailure.value,
        message: '积分已刷新，请手动重试当前操作。'
      }
    }
    ElMessage.success('充值结果已返回，积分已刷新；请手动重试当前操作。')
  }).catch(() => {
    ElMessage.warning('充值结果已返回，但积分刷新失败，请手动刷新后重试。')
  })
})

function partitionStatusType(partition: DomainPlanPartition) {
  if (partition.status === 'ACTIVE') return 'success'
  if (partition.status === 'PAYMENT_PAUSED') return 'warning'
  return 'danger'
}

function migrationTargets(partition: DomainPlanPartition) {
  const now = Date.parse(overview.value?.serverNow || overview.value?.serverTime || '')
  return partitions.value.filter(candidate =>
    candidate.id !== partition.id
    && candidate.planId === partition.planId
    && candidate.status === 'ACTIVE'
    && (candidate.quotaLimit === null || candidate.quotaUsed < candidate.quotaLimit)
    && (!Number.isFinite(now) || !Number.isFinite(Date.parse(candidate.expireAt)) || Date.parse(candidate.expireAt) > now)
  )
}

function migrationDomains(partition: DomainPlanPartition) {
  return domains.value.filter(domain =>
    domain.partitionId === partition.id && domain.status !== 'DELETED'
  )
}

function partitionCanRenew(partition: DomainPlanPartition) {
  return !partitionRenewBlockMessage(partition)
}

function partitionRenewBlockMessage(partition: DomainPlanPartition) {
  if (partition.status === 'CLOSED') return '分区已关闭，不能续费或修改自动续费。'
  if (partition.status === 'EXPIRED' && partition.quotaUsed === 0) {
    return '空分区已到期，不再续期；如需继续使用，请购买新分区。'
  }
  return ''
}

async function renewPartition(partition: DomainPlanPartition, periodCount = 1) {
  const currentActionKey = `partition-renew-${partition.id}`
  if (renewingPartitionIds.has(partition.id)) return
  renewingPartitionIds.add(partition.id)
  actionKey.value = currentActionKey
  packageFailure.value = null
  const intent = partitionRenewIntent(partition.id, periodCount)
  try {
    applyPartition(await pointApi.renewPartition(
      partition.id,
      periodCount,
      intent.key
    ))
    partitionRenewIntentKeys.delete(intent.signature)
    persistPartitionRenewIntentKeys()
    ElMessage.success(`${partitionDisplayName(partition)} 已续费 ${periodCount} 个月。`)
  } catch (error) {
    showPartitionFailure(error)
    actionKey.value = ''
    return
  } finally {
    renewingPartitionIds.delete(partition.id)
  }
  const [overviewResult, pointsResult] = await Promise.allSettled([
    pointApi.domainPlans(),
    points.refresh()
  ])
  if (overviewResult.status === 'fulfilled') {
    applyOverview(overviewResult.value)
  } else {
    ElMessage.warning('分区续费已成功，但最新套餐和域名状态刷新失败，请手动刷新。')
  }
  if (pointsResult.status === 'rejected') {
    ElMessage.warning('分区续费已成功，但积分余额刷新失败，请手动刷新。')
  }
  actionKey.value = ''
}

async function changePartitionAutoRenew(partition: DomainPlanPartition, value: string | number | boolean) {
  actionKey.value = `partition-auto-${partition.id}`
  packageFailure.value = null
  try {
    applyPartition(await pointApi.setPartitionAutoRenew(partition.id, Boolean(value)))
    ElMessage.success(Boolean(value) ? '已开启该分区自动续费。' : '已关闭该分区自动续费。')
  } catch (error) {
    showPartitionFailure(error)
  } finally {
    actionKey.value = ''
  }
}

async function migratePartition(partition: DomainPlanPartition) {
  const targetPartitionId = selectedMigrationTargetByPartition.value[partition.id]
  const domainId = selectedMigrationDomainByPartition.value[partition.id]
  const domain = migrationDomains(partition).find(item => item.domainId === domainId)
  const target = partitions.value.find(item => item.id === targetPartitionId)
  if (!targetPartitionId || !domainId || !domain || !target) {
    ElMessage.warning('请选择要迁移的域名和目标分区。')
    return
  }
  try {
    await ElMessageBox.confirm(
      partition.quotaMode === 'ROOT_DOMAIN'
        ? `将把 ${domain.domain || `域名 #${domain.domainId}`} 迁移到 ${partitionDisplayName(target)}；同根域名将由系统一起迁移。`
        : `将把 ${domain.domain || `域名 #${domain.domainId}`} 迁移到 ${partitionDisplayName(target)}。`,
      '确认迁移域名',
      { confirmButtonText: '确定迁移', cancelButtonText: '返回' }
    )
  } catch {
    return
  }
  actionKey.value = `partition-move-${partition.id}`
  packageFailure.value = null
  try {
    applyPartition(await pointApi.movePartitionDomains(partition.id, targetPartitionId, [domainId]))
  } catch (error) {
    showPartitionFailure(error)
    actionKey.value = ''
    return
  }
  try {
    applyOverview(await pointApi.domainPlans())
    ElMessage.success('域名已迁移到目标分区；原分区有效期和已付金额保持不变。')
  } catch {
    ElMessage.warning('域名迁移已成功，但最新分区归属刷新失败，请手动刷新。')
  } finally {
    actionKey.value = ''
  }
}

function beginPurchaseLock() {
  purchaseLockedUntil.value = Date.now() + 10000
  purchaseLockSeconds.value = 10
  if (purchaseLockTimer) window.clearInterval(purchaseLockTimer)
  purchaseLockTimer = window.setInterval(() => {
    purchaseLockSeconds.value = Math.max(0, Math.ceil((purchaseLockedUntil.value - Date.now()) / 1000))
    if (!purchaseLockSeconds.value && purchaseLockTimer) {
      window.clearInterval(purchaseLockTimer)
      purchaseLockTimer = undefined
    }
  }, 250)
}

function purchaseIntent(domainId: number, selection: DomainPackageSelection) {
  const selectionIdentity = selection.mode === 'EXISTING'
    ? `existing:${selection.partitionId}`
    : `new:${selection.planId}:${selection.periodCount}:${selection.autoRenew ? 1 : 0}`
  const signature = `${domainId}:${selectionIdentity}`
  const domainPrefix = `${domainId}:`
  for (const storedSignature of domainPurchaseIntentKeys.keys()) {
    if (storedSignature.startsWith(domainPrefix) && storedSignature !== signature) {
      domainPurchaseIntentKeys.delete(storedSignature)
    }
  }
  const key = domainPurchaseIntentKeys.get(signature)
    || `domain-plan-${domainId}-${selectionIdentity.replaceAll(':', '-')}-${Date.now()}-${Math.random().toString(36).slice(2)}`
  domainPurchaseIntentKeys.set(signature, key)
  persistDomainPurchaseIntentKeys()
  return { signature, key }
}

async function renewFree(domain: DomainPlanItem) {
  actionKey.value = `free-${domain.domainId}`
  try {
    applyOverview(await pointApi.renewFreeDomain(domain.domainId))
    ElMessage.success('免费套餐已续期 7 天。')
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '免费套餐续期失败，请稍后重试。'))
  } finally {
    actionKey.value = ''
  }
}

async function changeDailyAutoRenew(domain: DomainPlanItem, value: string | number | boolean) {
  actionKey.value = `daily-${domain.domainId}`
  try {
    applyOverview(await pointApi.setDomainDailyAutoRenew(domain.domainId, Boolean(value)))
    ElMessage.success(Boolean(value) ? '已开启每日积分续费。' : '已关闭每日积分续费。')
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '每日积分续费设置失败，请稍后重试。'))
  } finally {
    actionKey.value = ''
  }
}

async function changeMonthlyAutoRenew(domain: DomainPlanItem, value: string | number | boolean) {
  actionKey.value = `monthly-auto-${domain.domainId}`
  try {
    applyOverview(await pointApi.setDomainMonthlyAutoRenew(domain.domainId, Boolean(value)))
    ElMessage.success(Boolean(value) ? '已开启购买套餐自动续费。' : '已关闭购买套餐自动续费。')
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '自动续费设置失败，请稍后重试。'))
  } finally {
    actionKey.value = ''
  }
}

async function purchaseDomain(domain: DomainPlanItem) {
  const currentActionKey = `buy-${domain.domainId}`
  if (actionKey.value === currentActionKey) {
    ElMessage.warning('当前套餐操作正在处理中，请勿重复提交。')
    return
  }
  if (activePush.value) {
    ElMessage.warning('专属定制套餐生效期间，免费套餐和购买套餐入口已隐藏。')
    return
  }
  if (purchaseLocked.value) {
    ElMessage.warning(`操作处理中，请等待 ${purchaseLockSeconds.value} 秒后再试。`)
    return
  }
  if (points.summary?.spendingFrozen) {
    ElMessage.warning('积分消费已冻结，当前无法创建消费订单。')
    return
  }
  const option = selectedPackageOption(domain)
  const plan = selectedMonthlyPlan(domain)
  const selection = selectedPackageSelection(domain)
  if (!option || !plan || !selection) {
    ElMessage.warning('后台暂未配置可购买套餐。')
    return
  }
  if (option.disabled) {
    ElMessage.warning(packageSelectionHint(domain))
    return
  }
  const assigned = assignedRootPartitionForDomain(domain.domain, overview.value, option.planId)
  if (assigned && (selection.mode !== 'EXISTING' || selection.partitionId !== assigned.id)) {
    selectedPackageByDomain.value[domain.domainId] = `PARTITION:EXISTING:${assigned.id}`
    ElMessage.warning(`同一根域名必须继续使用 ${partitionDisplayName(assigned)}，已恢复为原分区。`)
    return
  }
  const months = selectedPeriods(domain.domainId)
  const payable = money(quote(plan, months))
  actionKey.value = currentActionKey
  try {
    if (selection.mode === 'EXISTING') {
      await ElMessageBox.confirm(
        `将域名 ${domain.domain} 绑定到 ${option.label}，使用已有额度，本次不扣积分。`,
        '确认绑定套餐分区',
        { confirmButtonText: '确定绑定', cancelButtonText: '返回' }
      )
    } else {
      await ElMessageBox.confirm(
        `将为域名 ${domain.domain} 购买 ${plan.planName}（新）${months} 个月，一次性扣除 ${payable} 积分。`,
        '确认购买新分区',
        { confirmButtonText: '确认购买', cancelButtonText: '返回', type: 'warning' }
      )
    }
  } catch {
    actionKey.value = ''
    return
  }
  beginPurchaseLock()
  const intent = purchaseIntent(domain.domainId, selection)
  try {
    applyOverview(await pointApi.assignDomainPackagePartition(
      domain.domainId,
      selection,
      intent.key
    ))
    domainPurchaseIntentKeys.delete(intent.signature)
    persistDomainPurchaseIntentKeys()
    ElMessage.success(selection.mode === 'EXISTING'
      ? '域名已绑定到已有套餐分区，本次未扣积分。'
      : '新套餐分区已购买，当前域名已生效。')
    if (selection.mode === 'NEW') await points.refresh()
  } catch (error) {
    showPartitionFailure(error)
  } finally {
    actionKey.value = ''
  }
}

async function pauseDomain(domain: DomainPlanItem) {
  actionKey.value = `pause-${domain.domainId}`
  try {
    applyOverview(await pointApi.pauseDomainPlan(domain.domainId))
    ElMessage.success('域名套餐已暂停。')
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '暂停失败，请稍后重试。'))
  } finally {
    actionKey.value = ''
  }
}

async function resumeDomain(domain: DomainPlanItem) {
  actionKey.value = `resume-${domain.domainId}`
  try {
    applyOverview(await pointApi.resumeDomainPlan(domain.domainId))
    ElMessage.success('域名套餐已恢复。')
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '恢复失败，请稍后重试。'))
  } finally {
    actionKey.value = ''
  }
}

onMounted(() => {
  if (hasConsoleSession()) void load()
})
onBeforeUnmount(() => {
  if (purchaseLockTimer) window.clearInterval(purchaseLockTimer)
  stopRechargeReturn()
})
</script>

<template>
  <section class="package-page" v-loading="loading">
    <header class="package-hero">
      <div>
        <p class="eyebrow">客户套餐</p>
        <h1>套餐与服务</h1>
        <p>现在只保留三类套餐：免费套餐、专属定制套餐、按域名购买套餐。</p>
      </div>
      <RouterLink class="point-balance" to="/points">
        <span>可用积分</span>
        <strong>{{ guestMode ? '--' : money(points.summary?.balance) }}</strong>
        <small>查看积分明细</small>
      </RouterLink>
    </header>

    <el-alert
      v-if="points.summary?.spendingFrozen"
      title="积分消费已冻结，当前仅允许查看，购买套餐扣费已冻结。"
      type="warning"
      show-icon
      :closable="false"
    />

    <section v-if="packageFailure" class="package-failure" data-test="partition-failure">
      <strong>操作未完成</strong>
      <p>{{ packageFailure.message }}</p>
      <div class="package-failure-actions">
        <template v-if="packageFailure.rechargeRequired">
          <el-button type="primary" @click="chooseRechargeNetwork('BSC')">BSC 充值</el-button>
          <el-button type="success" @click="chooseRechargeNetwork('TRX')">TRX 充值</el-button>
        </template>
        <RouterLink v-if="packageFailure.contactRequired" to="/support?topic=domain-package-partition">联系我们</RouterLink>
        <el-button v-if="packageFailure.returnToEdit" @click="packageFailure = null">返回修改</el-button>
      </div>
    </section>

    <section v-if="activePush" class="push-plan" :class="{ expired: activePush.expired }">
      <div class="push-badge">专属定制</div>
      <div>
        <span>客户级专属套餐已生效</span>
        <h2>{{ activePush.planName }}</h2>
        <p v-if="activePush.expired && activePush.serviceContinuesAfterExpiry">
          专属套餐已到期，现有加速服务继续运行，直到管理员调整或解除套餐。
        </p>
        <p v-else-if="activePush.expired && activePush.accelerationPaused">
          专属套餐已到期，加速服务已暂停，请联系管理员处理。
        </p>
        <p v-else-if="activePush.expired">专属套餐已到期，请联系管理员确认后续服务安排。</p>
        <p v-else>{{ activePush.remark || '后台已为当前账号推送专属定制套餐，所有域名统一按专属规则展示。' }}</p>
      </div>
      <div class="push-expire">
        <span>推送时间：{{ dateText(activePush.assignedAt) }}</span>
        <strong>到期时间：{{ dateText(activePush.expireAt) }}</strong>
        <small v-if="activePush.graceUntil">自动解除：{{ dateText(activePush.graceUntil) }}</small>
      </div>
    </section>

    <template v-else>
      <section class="free-cdn-entry">
        <div>
          <p class="eyebrow">免费套餐</p>
          <h2>免费套餐</h2>
          <span>免费套餐保留原有域名接入流程，不产生积分扣费。</span>
        </div>
        <RouterLink :to="{ path: '/domains', query: { applyFreeCdn: '1' } }">前往申请</RouterLink>
      </section>

      <div class="section-title">
        <div>
          <p class="eyebrow">购买套餐</p>
          <h2>购买套餐按独立分区生效</h2>
        </div>
        <div class="discount-note">
          <b>按月扣费</b>
          <b>满 6 月 9 折</b>
          <b>满 12 月 8.5 折</b>
        </div>
      </div>
    </template>

    <section v-if="partitions.length && !activePush" class="partition-section" data-test="partition-section">
      <div class="section-title compact">
        <div>
          <p class="eyebrow">购买分区</p>
          <h2>已购买的套餐分区</h2>
        </div>
        <RouterLink class="partition-buy-link" to="/domains">购买新分区</RouterLink>
      </div>
      <p class="partition-occupancy-note">异常或暂停域名仍占用额度，请及时删除不再使用的域名。</p>
      <div class="partition-grid">
        <article v-for="partition in partitions" :key="partition.id" class="partition-card">
          <div class="partition-card-head">
            <div>
              <span>{{ partition.planName }}</span>
              <h3>{{ partitionDisplayName(partition) }}</h3>
            </div>
            <el-tag :type="partitionStatusType(partition)">{{ partitionStatusLabel(partition.status) }}</el-tag>
          </div>
          <div class="partition-facts">
            <div><span>{{ quotaModeLabel(partition.quotaMode) }}</span><b>域名 {{ partition.quotaUsed }}/{{ partition.quotaLimit ?? '不限量' }}</b></div>
            <div><span>到期时间</span><b>{{ dateText(partition.expireAt) }}</b></div>
            <div><span>套餐价格</span><b>{{ money(partition.pointPrice) }} 积分/{{ periodLabel(partition.periodMode) }}</b></div>
          </div>
          <div class="partition-renew-row">
            <span>独立自动续费</span>
            <el-switch
              :data-test="`partition-auto-${partition.id}`"
              :model-value="partition.autoRenew"
              :loading="actionKey === 'partition-auto-' + partition.id"
              :disabled="!partitionCanRenew(partition)"
              @change="changePartitionAutoRenew(partition, $event)"
            />
            <el-button
              :data-test="`partition-renew-${partition.id}`"
              :loading="actionKey === 'partition-renew-' + partition.id"
              :disabled="!partitionCanRenew(partition)"
              @click="renewPartition(partition)"
            >续费</el-button>
          </div>
          <p v-if="partitionRenewBlockMessage(partition)" class="partition-expired-empty">
            {{ partitionRenewBlockMessage(partition) }}
          </p>
          <div class="partition-move-row">
            <el-select
              v-model="selectedMigrationDomainByPartition[partition.id]"
              :data-test="`partition-domain-${partition.id}`"
              placeholder="选择要迁移的域名"
            >
              <el-option
                v-for="domain in migrationDomains(partition)"
                :key="domain.domainId"
                :label="domain.domain || `域名 #${domain.domainId}`"
                :value="domain.domainId"
              />
            </el-select>
            <el-select v-model="selectedMigrationTargetByPartition[partition.id]" placeholder="选择目标分区">
              <el-option
                v-for="target in migrationTargets(partition)"
                :key="target.id"
                :label="partitionDisplayName(target)"
                :value="target.id"
              />
            </el-select>
            <el-button
              :data-test="`partition-move-${partition.id}`"
              :loading="actionKey === 'partition-move-' + partition.id"
              :disabled="!migrationTargets(partition).length || !migrationDomains(partition).length"
              @click="migratePartition(partition)"
            >迁移域名</el-button>
          </div>
          <div v-if="partition.manualResumeRequired" class="partition-manual-resume">
            <p>续费完成后仍需手动启用加速，系统不会自动恢复域名。</p>
            <el-button
              v-for="domain in migrationDomains(partition).filter(item => item.status === 'PROTECTION')"
              :key="`resume-${domain.domainId}`"
              :loading="actionKey === 'resume-' + domain.domainId"
              @click="resumeDomain(domain)"
            >手动启用 {{ domain.domain || `域名 #${domain.domainId}` }}</el-button>
          </div>
        </article>
      </div>
    </section>

    <section class="domain-section">
      <div class="section-title compact">
        <div>
          <p class="eyebrow">域名套餐</p>
          <h2>域名套餐</h2>
        </div>
        <el-button :loading="loading" :disabled="guestMode" @click="load">{{ guestMode ? '登录后查看' : '刷新' }}</el-button>
      </div>

      <section v-if="domains.length" class="domain-grid">
        <article v-for="domain in domains" :key="domain.domainId" class="domain-card">
          <div class="domain-card-head">
            <div>
              <strong>{{ domain.domain }}</strong>
              <span>{{ billingLabel(domain) }}</span>
            </div>
            <el-tag :type="statusType(domain)">{{ statusLabel(domain) }}</el-tag>
          </div>

          <div v-if="activePush" class="domain-facts">
            <div>
              <span>专属套餐</span>
              <b>{{ domain.planName || activePush.planName }}</b>
            </div>
            <div>
              <span>生效时间</span>
              <b>{{ dateText(activePush.assignedAt) }}</b>
            </div>
            <div>
              <span>到期时间</span>
              <b>{{ dateText(activePush.expireAt) }}</b>
            </div>
          </div>

          <div v-else class="domain-facts">
            <div>
              <span>免费剩余</span>
              <b>{{ Math.ceil((domain.freeRemainingSeconds || 0) / 86400) }} 天</b>
            </div>
            <div>
              <span>购买到期</span>
              <b>{{ dateText(domain.paidUntil) }}</b>
            </div>
            <div>
              <span>保护期</span>
              <b>{{ dateText(domain.protectionUntil) }}</b>
            </div>
          </div>

          <p v-if="domain.suspensionReason" class="domain-warning">原因：{{ suspensionReasonLabel(domain.suspensionReason) }}</p>

          <!-- V0.1.129 | 2026-10-02 15:28 CST | parent f142a195884a186a912053e3a5175725bad58984:
               A suspension explanation must not hide the existing recovery/renewal actions. -->
          <template>
            <div v-if="domain.billingMode === 'FREE'" class="inline-action">
              <span>每日积分续费</span>
              <el-switch
                :model-value="domain.dailyAutoRenew"
                :loading="actionKey === 'daily-' + domain.domainId"
                @change="changeDailyAutoRenew(domain, $event)"
              />
              <el-button
                :loading="actionKey === 'free-' + domain.domainId"
                :disabled="!domain.freeRenewAvailable"
                @click="renewFree(domain)"
              >续期免费 7 天</el-button>
            </div>

            <div v-if="domain.billingMode === 'MONTHLY'" class="inline-action">
              <span>购买套餐自动续费</span>
              <el-switch
                :model-value="domain.monthlyAutoRenew"
                :loading="actionKey === 'monthly-auto-' + domain.domainId"
                @change="changeMonthlyAutoRenew(domain, $event)"
              />
              <el-button v-if="domain.status === 'ACTIVE'" :loading="actionKey === 'pause-' + domain.domainId" @click="pauseDomain(domain)">暂停</el-button>
              <el-button v-else :loading="actionKey === 'resume-' + domain.domainId" @click="resumeDomain(domain)">恢复</el-button>
            </div>

            <div v-if="domain.billingMode === 'PARTITION'" class="inline-action">
              <span>所属分区：{{ domain.partitionDisplayName || domain.planName || '未知分区' }}</span>
              <el-button
                v-if="domain.status === 'ACTIVE'"
                :loading="actionKey === 'pause-' + domain.domainId"
                @click="pauseDomain(domain)"
              >暂停</el-button>
              <el-button
                v-else
                :data-test="`partition-manual-resume-${domain.domainId}`"
                :loading="actionKey === 'resume-' + domain.domainId"
                @click="resumeDomain(domain)"
              >手动启用</el-button>
            </div>

            <div v-if="domain.billingMode !== 'PARTITION' && domain.status !== 'DELETED'" class="purchase-box">
              <el-select
                v-model="selectedPackageByDomain[domain.domainId]"
                :data-test="`domain-package-select-${domain.domainId}`"
                placeholder="选择套餐分区"
                :disabled="!packageOptions.length"
                @change="handlePackageSelectionChange(domain, $event)"
              >
                <el-option
                  v-for="option in packageOptions"
                  :key="option.key"
                  :label="option.label"
                  :value="option.key"
                  :disabled="option.disabled"
                />
              </el-select>
              <p class="package-selection-hint">{{ packageSelectionHint(domain) }}</p>
              <el-radio-group
                v-if="selectedPackageOption(domain)?.isNew"
                v-model="periodsByDomain[domain.domainId]"
                class="period-options"
              >
                <el-radio-button v-for="option in periodOptions" :key="`${domain.domainId}-${option}`" :value="option">
                  {{ option }} 月
                </el-radio-button>
              </el-radio-group>
              <div v-if="selectedPackageOption(domain)?.isNew" class="quote">
                <span>一次性应扣</span>
                <b>{{ money(quote(selectedMonthlyPlan(domain), selectedPeriods(domain.domainId))) }} 积分</b>
              </div>
              <el-button
                :data-test="`domain-package-submit-${domain.domainId}`"
                class="buy-button"
                type="primary"
                :loading="actionKey === 'buy-' + domain.domainId"
                :disabled="!selectedPackageOption(domain) || selectedPackageOption(domain)?.disabled || points.summary?.spendingFrozen || purchaseLocked"
                @click="purchaseDomain(domain)"
              >
                {{ purchaseLocked
                  ? `请等待 ${purchaseLockSeconds} 秒`
                  : selectedPackageOption(domain)?.isNew
                    ? '购买新分区并绑定'
                    : '绑定到此套餐分区' }}
              </el-button>
            </div>
          </template>
        </article>
      </section>
      <el-empty v-else :description="domainEmptyDescription" />
    </section>
  </section>
</template>

<style scoped>
.package-page { display: grid; gap: 20px; }
.eyebrow { margin: 0; color: #267b72; font-size: 11px; font-weight: 900; letter-spacing: .17em; }
.package-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; padding: 28px; border-radius: 18px; color: #eff9f7; background: radial-gradient(circle at 84% 18%, rgba(82, 202, 174, .28), transparent 34%), linear-gradient(125deg, #0f2731, #164c45 58%, #2a6958); box-shadow: 0 20px 50px rgba(18, 53, 47, .16); }
.package-hero h1 { margin: 5px 0 8px; font-size: 32px; }
.package-hero > div > p:last-child { margin: 0; color: #bddbd3; }
.package-hero .eyebrow { color: #7adfc9; }
.point-balance { display: grid; min-width: 175px; padding: 16px 20px; border: 1px solid rgba(255,255,255,.22); border-radius: 14px; color: #fff; background: rgba(255,255,255,.1); text-decoration: none; }
.point-balance span, .point-balance small { color: #c9e2dc; font-size: 12px; }
.point-balance strong { margin: 4px 0; font-size: 26px; }
.push-plan { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 20px; padding: 24px; border: 1px solid #d4b060; border-radius: 16px; background: linear-gradient(110deg, #fffdf4, #f4ead0); }
.push-plan.expired { border-color: #ed9c88; background: linear-gradient(110deg, #fff6f1, #fff1e8); }
.push-badge { padding: 10px 12px; border-radius: 10px; color: #fff; background: #9b6c12; font-size: 12px; font-weight: 900; }
.push-plan.expired .push-badge { background: #b64b34; }
.push-plan h2 { margin: 5px 0; }
.push-plan p { margin: 0; color: #756841; }
.push-expire { display: grid; gap: 5px; color: #775f2d; text-align: right; }
.free-cdn-entry { display: flex; align-items: center; justify-content: space-between; padding: 22px; border: 1px solid #e6d39c; border-radius: 15px; background: #fffaf0; }
.free-cdn-entry h2 { margin: 5px 0; }
.free-cdn-entry span { color: #806f4d; }
.free-cdn-entry a { padding: 11px 16px; border-radius: 10px; color: #fff; background: #183f31; text-decoration: none; font-weight: 800; }
.section-title { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; }
.section-title.compact { align-items: center; }
.section-title h2 { margin: 4px 0 0; color: #19364a; }
.discount-note { display: flex; flex-wrap: wrap; gap: 8px; }
.discount-note b { padding: 7px 11px; border-radius: 999px; color: #176a60; background: #e6f5f1; font-size: 12px; }
.domain-section { display: grid; gap: 14px; }
.domain-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.domain-card { display: grid; gap: 16px; padding: 20px; border: 1px solid #dce8e4; border-radius: 16px; background: #fff; box-shadow: 0 12px 30px rgba(22, 50, 70, .06); }
.domain-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.domain-card-head div { display: grid; gap: 6px; }
.domain-card-head strong { color: #142c25; font-size: 18px; }
.domain-card-head span { color: #6b7d75; }
.domain-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.domain-facts div { display: grid; gap: 6px; min-height: 62px; padding: 12px; border: 1px solid #edf2ef; border-radius: 12px; background: #fbfdfc; }
.domain-facts span { color: #77867f; font-size: 12px; }
.domain-facts b { color: #193c32; font-size: 13px; word-break: break-word; }
.domain-warning { margin: 0; padding: 10px 12px; border: 1px solid #f4b1a5; border-radius: 10px; color: #c03327; background: #fff3f1; font-weight: 800; }
.domain-lock-note { padding: 13px 14px; border-radius: 12px; color: #755b22; background: #fff6d9; }
.inline-action { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; color: #486258; }
.purchase-box { display: grid; gap: 12px; padding: 14px; border: 1px solid #d6e8e1; border-radius: 14px; background: linear-gradient(180deg, #fbfffd, #f5fbf8); }
.period-options { display: flex; }
.period-options :deep(.el-radio-button) { flex: 1; }
.period-options :deep(.el-radio-button__inner) { width: 100%; }
.quote { display: flex; justify-content: space-between; color: #667984; }
.quote b { color: #19776c; }
.buy-button { width: 100%; }
.package-failure { padding: 16px; border: 1px solid #efaa9d; border-radius: 14px; color: #7f2b20; background: #fff4f1; }
.package-failure p { margin: 6px 0 12px; }
.package-failure-actions, .partition-renew-row, .partition-move-row { display: flex; align-items: center; gap: 10px; }
.package-failure-actions a, .partition-buy-link, .partition-manual-resume a { color: #176a60; font-weight: 800; }
.partition-section { display: grid; gap: 14px; }
.partition-occupancy-note { margin: 0; color: #9a5a22; }
.partition-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.partition-card { display: grid; gap: 14px; padding: 20px; border: 1px solid #d6e8e1; border-radius: 16px; background: #fff; }
.partition-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.partition-card-head span { color: #718279; font-size: 12px; }
.partition-card-head h3 { margin: 4px 0 0; color: #173a31; font-size: 21px; }
.partition-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.partition-facts div { display: grid; gap: 5px; padding: 10px; border-radius: 10px; background: #f6faf8; }
.partition-facts span { color: #728279; font-size: 12px; }
.partition-facts b { color: #24463d; font-size: 13px; }
.partition-move-row :deep(.el-select) { flex: 1; }
.partition-manual-resume { padding: 12px; border-radius: 10px; color: #855722; background: #fff4dc; }
.partition-manual-resume p { margin: 0 0 7px; }
@media (max-width: 960px) { .domain-grid, .partition-grid { grid-template-columns: 1fr; } .push-plan { grid-template-columns: 1fr; } .push-expire { text-align: left; } }
@media (max-width: 680px) { .package-hero, .section-title, .free-cdn-entry { align-items: flex-start; flex-direction: column; } .point-balance { width: 100%; box-sizing: border-box; } .domain-facts { grid-template-columns: 1fr; } }
</style>
